import { Autocomplete, Chip, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isNoValueOperator } from "../../../../../common/FiltersFormEngine/operatorRules";
import { QueryAction } from "../../../../../common/FiltersFormEngine/queryReducer";
import { flattenQueryFilters, matchesFormRow } from "../../../../../common/FiltersFormEngine/querySelectors";
import { RowFilter } from "../../../../../common/FiltersFormEngine/types";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import { getMenuAvailableFilters } from "../../../../../context/FiltersProvider/selectors/menuSelectors";
import { FilterOperator } from "../../../../../context/FiltersProvider/types";
import { FilterContexType } from "../../../../layout/ViewHeader";

type Props = {
	item: RowFilter;
	filterContext?: FilterContexType;
	dispatch: React.Dispatch<QueryAction>;
	readOnly?: boolean;
};

type OperatorUI = FilterOperator | "";

const toSingleValue = (value: unknown): string => {
	if (value == null) return "";
	if (Array.isArray(value)) return String(value[0] ?? "");
	return String(value);
};

const FilterRow = ({ item, filterContext, dispatch, readOnly }: Props) => {
	const { t } = useTranslation("common");
	const { registry, appliedFilters } = useFiltersRegistry();

	const selectedField = item.filter ? registry?.byId?.[item.filter] : null;
	const operators = selectedField?.field?.operators ?? [];
	const selectedOperator = operators.find((o) => o.operator === item.operator) ?? operators[0];
	const isMulti = selectedOperator?.supportsMultiSelect;
	const isTextInput = selectedField?.field?.inputType === "text";
	const isValueDisabled = !selectedField || isNoValueOperator(item.operator as FilterOperator);

	const applied = appliedFilters.payload;

	const flat = flattenQueryFilters(applied);

	const isApplied = matchesFormRow(item, flat);

	const paramsFilterContext = {
		visibility: filterContext,
	};

	const filtersOption = getMenuAvailableFilters(registry, paramsFilterContext);

	return (
		<Stack direction="row" spacing={2} alignItems="center" width="100%">
			{/* Filter */}
			<Stack flex={2} sx={{ minWidth: 0 }}>
				<Autocomplete
					disabled={readOnly}
					size="small"
					options={filtersOption}
					value={selectedField ?? null}
					isOptionEqualToValue={(opt, val) => opt.id === val.id}
					getOptionLabel={(opt) => opt.name}
					onChange={(_, newValue) => {
						dispatch({
							type: "UPDATE_ROW",
							nodeId: item.id,
							payload: {
								filter: newValue?.id,
							},
						});
					}}
					renderInput={(params) => (
						<TextField {...params} label={t("filter")} placeholder={t("filter")} size="small" fullWidth />
					)}
					sx={{
						width: "100%",
						minWidth: 0,
						"& .MuiOutlinedInput-notchedOutline": {
							borderLeftWidth: isApplied ? "6px" : "1px",
							borderLeftColor: isApplied ? "primary" : undefined,
						},

						"& .MuiAutocomplete-inputRoot": {
							flexWrap: "nowrap",
							minWidth: 0,
						},
						"& .MuiAutocomplete-input": {
							minWidth: 0,
						},
					}}
				/>
			</Stack>

			{/* Operator */}
			<Stack flex={1} sx={{ minWidth: 0 }}>
				<Select
					size="small"
					fullWidth
					value={(item?.operator as OperatorUI) ?? ""}
					disabled={!selectedField || readOnly}
					onChange={(e) => {
						const nextOperator = e.target.value;

						dispatch({
							type: "UPDATE_ROW",
							nodeId: item.id,
							payload: {
								operator: nextOperator as any,
								value: "",
							},
						});
					}}
					displayEmpty
					renderValue={(selected) => {
						if (!selected) {
							return <Typography color="text.secondary">{t("operator")}</Typography>;
						}

						return operators.find((op) => op.operator === selected)?.label;
					}}
					sx={{
						width: "100%",
						minWidth: 0,

						"& .MuiSelect-select": {
							display: "block",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							width: "100%",
						},

						"& .MuiOutlinedInput-input": {
							minWidth: 0,
						},
					}}
				>
					{operators.map((op) => (
						<MenuItem key={op.operator} value={op.operator}>
							{op.label}
						</MenuItem>
					))}
				</Select>
			</Stack>

			{/* VALUE */}

			<Stack flex={2} sx={{ minWidth: 0 }}>
				{isTextInput ? (
					<TextField
						size="small"
						fullWidth
						disabled={isValueDisabled || readOnly}
						placeholder="Value"
						value={toSingleValue(item.value)}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_ROW",
								nodeId: item.id,
								payload: {
									value: e.target.value,
								},
							})
						}
						sx={{
							width: "100%",
							minWidth: 0,
							"& input": {
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							},
						}}
					/>
				) : isMulti ? (
					<Autocomplete
						multiple
						limitTags={readOnly ? 999 : 2} // 999 is just a big number to cover all the selected filters to be shown
						disableCloseOnSelect
						size="small"
						options={selectedField?.field?.options ?? []}
						disabled={isValueDisabled || readOnly}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
						value={
							selectedField?.field?.options?.filter((opt) =>
								Array.isArray(item.value) ? item.value.includes(opt.id) : false
							) ?? []
						}
						onChange={(_, newValue) => {
							dispatch({
								type: "UPDATE_ROW",
								nodeId: item.id,
								payload: {
									value: newValue.map((v) => v.id),
								},
							});
						}}
						renderTags={(tagValue, getTagProps) =>
							tagValue.map((option, index) => {
								const { key, ...tagProps } = getTagProps({ index });

								return (
									<Chip key={key} label={option.label} size="small" color="primary" {...tagProps} />
								);
							})
						}
						renderInput={(params) => <TextField {...params} label={t("value")} fullWidth />}
						sx={{
							"& .MuiAutocomplete-inputRoot": {
								paddingTop: "4px !important",
								paddingBottom: "4px !important",
							},
							"& .MuiChip-root": {
								height: 24,
								fontSize: 12,
							},
							"& .MuiAutocomplete-tag": {
								margin: "0 0 0 2px",
							},
							"& .MuiOutlinedInput-root": {
								minHeight: 40,
								overflow: "hidden",
							},
							minWidth: 0,
							"& input": {
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							},
						}}
					/>
				) : (
					<Select
						size="small"
						fullWidth
						displayEmpty
						disabled={isValueDisabled || readOnly}
						value={toSingleValue(item.value)}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_ROW",
								nodeId: item.id,
								payload: {
									value: e.target.value,
								},
							})
						}
						renderValue={(selected) => {
							if (!selected) {
								return <Typography color="text.secondary">{t("value")}</Typography>;
							}

							return (
								selectedField?.field?.options?.find((o) => String(o.id) === String(selected))?.label ??
								selected
							);
						}}
						sx={{
							width: "100%",
							minWidth: 0,

							"& .MuiSelect-select": {
								display: "block",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								width: "100%",
							},

							"& .MuiOutlinedInput-input": {
								minWidth: 0,
							},
						}}
					>
						{selectedField?.field?.options?.map((opt) => (
							<MenuItem key={opt.id} value={String(opt.id)}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				)}
			</Stack>
		</Stack>
	);
};

export default FilterRow;
